import { useEffect, useState } from "react";
import { api, formatPrice, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const STATUSES = ["Pending", "Confirmed", "Preparing", "Ready", "Out for Delivery", "Delivered", "Cancelled"];
const PAYMENT_STATUSES = ["Payment Pending", "Payment Verified", "Payment Rejected"];

const payClass = (s) =>
  s === "Payment Verified" ? "bg-green-100 text-green-700" : s === "Payment Rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = q
    ? orders.filter((o) =>
        [o.order_id, o.customer?.name, o.customer?.phone].some((v) => (v || "").toLowerCase().includes(q))
      )
    : orders;

  const load = () => api.get("/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const changeStatus = async (order_id, status) => {
    const { data } = await api.put(`/orders/${order_id}/status`, { status });
    toast.success(`Status → ${status}`);
    setOrders((prev) => prev.map((o) => (o.order_id === order_id ? data : o)));
    setSelected((s) => (s && s.order_id === order_id ? data : s));
  };

  const changePayment = async (order_id, payment_status) => {
    const { data } = await api.put(`/orders/${order_id}/payment`, { payment_status });
    toast.success(`Payment → ${payment_status}`);
    setOrders((prev) => prev.map((o) => (o.order_id === order_id ? data : o)));
    setSelected((s) => (s && s.order_id === order_id ? data : s));
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-brand-dark">Orders</h1>
      <p className="text-brand-muted">{q ? `${filtered.length} of ${orders.length}` : orders.length} orders</p>

      <input
        type="text"
        data-testid="orders-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by Customer Name, Phone Number or Order ID"
        className="mt-4 w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-gold"
      />

      <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-gray-500">
            <tr>
              <th className="p-4">Order ID</th>
              <th className="p-4">Date</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Items</th>
              <th className="p-4">Total</th>
              <th className="p-4">Status</th>
              <th className="p-4">Payment</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr
                key={o.order_id}
                data-testid={`admin-order-${o.order_id}`}
                onClick={() => setSelected(o)}
                className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="p-4 font-medium text-brand">{o.order_id}</td>
                <td className="whitespace-nowrap p-4 text-gray-600">{formatDate(o.created_at)}</td>
                <td className="p-4">{o.customer?.name}<br /><span className="text-xs text-gray-400">{o.customer?.phone}</span></td>
                <td className="p-4">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                <td className="p-4">{formatPrice(o.total)}</td>
                <td className="p-4">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${o.status === "Cancelled" ? "bg-red-100 text-red-600" : "bg-brand-green/10 text-brand-green"}`}>{o.status}</span>
                </td>
                <td className="p-4">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${payClass(o.payment_status)}`}>{o.payment_status || "Payment Pending"}</span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-gray-400">{q ? "No matching orders" : "No orders yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6" data-testid="order-detail-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-brand-dark">Order {selected.order_id}</h2>
              <button onClick={() => setSelected(null)} className="rounded-full p-2 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <p data-testid="order-date" className="mt-1 text-sm text-gray-500">Placed on {formatDate(selected.created_at)}</p>

            <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm">
              <p className="font-semibold text-brand-dark">{selected.customer.name}</p>
              <p className="text-gray-600">Phone: {selected.customer.phone}</p>
              {selected.customer.whatsapp && <p className="text-gray-600">WhatsApp: {selected.customer.whatsapp}</p>}
              <p className="text-gray-600">{selected.customer.address}, {selected.customer.city} - {selected.customer.pincode}</p>
              {selected.notes && <p className="mt-2 italic text-gray-600">Note: {selected.notes}</p>}
            </div>

            <div className="mt-4">
              <h3 className="font-heading text-lg text-brand-dark">Items</h3>
              <ul className="mt-2 space-y-2 text-sm">
                {selected.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between border-b border-gray-100 pb-2">
                    <span>
                      {i.name} ({i.variant_label}) ×{i.quantity}
                      {i.customization && <span className="block text-xs italic text-brand">{i.customization_label ? `${i.customization_label}: ` : ""}“{i.customization}”</span>}
                    </span>
                    <span className="font-medium">{formatPrice(i.price * i.quantity)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-right text-sm text-gray-600">Subtotal: {formatPrice(selected.subtotal ?? selected.total)}</p>
              <p className="text-right text-sm text-gray-600">Delivery: {formatPrice(selected.delivery_charge ?? 0)}</p>
              <p className="text-right font-semibold text-brand-dark">Total: {formatPrice(selected.total)}</p>
            </div>

            <div className="mt-4 rounded-xl border border-brand-line bg-brand-gold-light/20 p-4 text-sm">
              <h3 className="font-heading text-lg text-brand-dark">Payment</h3>
              <p className="mt-1 text-gray-600">Method: <span className="font-medium text-brand-dark">{selected.payment_method || "UPI"}</span></p>
              <p className="text-gray-600">UTR / Ref: <span data-testid="order-utr" className="font-medium text-brand-dark">{selected.utr || "—"}</span></p>
              <p className="text-gray-600">Amount: <span className="font-medium text-brand-dark">{formatPrice(selected.total)}</span></p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-gray-600">Status:</span>
                <span data-testid="order-payment-status" className={`rounded-full px-3 py-1 text-xs font-medium ${payClass(selected.payment_status)}`}>{selected.payment_status || "Payment Pending"}</span>
              </div>
              <label className="mb-1.5 mt-3 block text-sm font-medium text-gray-600">Update Payment Status</label>
              <select
                data-testid="order-payment-select"
                value={selected.payment_status || "Payment Pending"}
                onChange={(e) => changePayment(selected.order_id, e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold"
              >
                {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-400">Manual verification: confirm the payment in your bank/UPI app, then set the status here. The UTR is only the customer's reference number.</p>
            </div>

            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-medium text-gray-600">Update Status</label>
              <select
                data-testid="order-status-select"
                value={selected.status}
                onChange={(e) => changeStatus(selected.order_id, e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
